import { ApplicationConfig, importProvidersFrom, provideZoneChangeDetection } from "@angular/core";
import { provideRouter, withComponentInputBinding, withRouterConfig } from "@angular/router";

import { StoreAuthModule } from "@/app/stores/auth/store-auth.module";
import { environment } from "@environments/environment";
import { registerLocaleData } from "@angular/common";
import { provideHttpClient, withInterceptors } from "@angular/common/http";
import en from "@angular/common/locales/en";
import { FormsModule } from "@angular/forms";
import { provideAnimationsAsync } from "@angular/platform-browser/animations/async";
import {
  LoadingOutline,
  LogoutOutline,
  LoginOutline,
  PlusOutline,
  SyncOutline,
  UserOutline,
  UploadOutline,
  CopyOutline,
  LeftOutline,
  RightOutline,
  EditOutline,
  PhoneOutline,
  MailOutline,
  FileTextOutline,
  DownloadOutline,
  InfoCircleOutline,
  ArrowLeftOutline,
  ArrowRightOutline,
  ArrowDownOutline,
  ArrowUpOutline,
  MoreOutline,
  WarningOutline,
  ExclamationCircleFill,
  DeleteOutline,
  CaretDownOutline,
  BellOutline,
  ExportOutline,
  DollarCircleOutline,
  CalendarOutline,
  CreditCardOutline,
  CloudUploadOutline,
  FileOutline,
  InboxOutline,
  UserAddOutline,
  FlagOutline,
  PercentageOutline,
  PlusCircleOutline,
  CloseCircleOutline,
  DownOutline,
  UpOutline,
  MoneyCollectOutline,
  BankOutline,
  SafetyCertificateOutline,
  CheckCircleFill,
  CheckOutline,
  PictureOutline,
  FilePdfOutline,
  ThunderboltFill,
  DeploymentUnitOutline,
  FileAddOutline,
  RobotOutline,
  LockOutline,
  UnorderedListOutline,
  EyeFill,
  SendOutline,
  PaperClipOutline,
  MessageOutline,
  CloseOutline,
  SettingOutline,
} from "@ant-design/icons-angular/icons";
import { AuthModule } from "@auth0/auth0-angular";
import { EffectsModule } from "@ngrx/effects";
import { StoreModule } from "@ngrx/store";
import { en_US, provideNzI18n } from "ng-zorro-antd/i18n";
import { NzIconModule } from "ng-zorro-antd/icon";
import { NzModalModule } from "ng-zorro-antd/modal";
import { NzNotificationModule } from "ng-zorro-antd/notification";
import { routes } from "./app.routes";
import { authInterceptor } from "./core/interceptors/auth.interceptor";
import { provideCharts, withDefaultRegisterables } from "ng2-charts";
import { StoreBusinessUsersModule } from "./stores/business-users/store-business-users.module";
import { StoreNotificationsModule } from "./stores/notifications/store-notifications.module";
import { providePostHogErrorHandler, providePostHogPageviewTracking } from "@handshake/core-fe";

registerLocaleData(en);

const icons = [
  ArrowRightOutline,
  LoadingOutline,
  SyncOutline,
  LogoutOutline,
  PlusOutline,
  UserOutline,
  UploadOutline,
  CopyOutline,
  LeftOutline,
  RightOutline,
  EditOutline,
  DeleteOutline,
  PhoneOutline,
  MailOutline,
  FileTextOutline,
  DownloadOutline,
  InfoCircleOutline,
  ArrowLeftOutline,
  ArrowRightOutline,
  ArrowDownOutline,
  ArrowUpOutline,
  ArrowLeftOutline,
  ArrowRightOutline,
  MoreOutline,
  WarningOutline,
  ExclamationCircleFill,
  CaretDownOutline,
  BellOutline,
  ExportOutline,
  LoginOutline,
  DollarCircleOutline,
  CalendarOutline,
  CreditCardOutline,
  CloudUploadOutline,
  FileOutline,
  InboxOutline,
  UserAddOutline,
  FlagOutline,
  PercentageOutline,
  PlusCircleOutline,
  CloseCircleOutline,
  DownOutline,
  UpOutline,
  MoneyCollectOutline,
  BankOutline,
  SafetyCertificateOutline,
  CheckCircleFill,
  CheckOutline,
  PictureOutline,
  FilePdfOutline,
  ThunderboltFill,
  DeploymentUnitOutline,
  FileAddOutline,
  RobotOutline,
  LockOutline,
  UnorderedListOutline,
  EyeFill,
  SendOutline,
  PaperClipOutline,
  MessageOutline,
  CloseOutline,
  SettingOutline,
];

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes, withComponentInputBinding(), withRouterConfig({ paramsInheritanceStrategy: "always" })),
    provideNzI18n(en_US),
    importProvidersFrom(FormsModule, NzNotificationModule, StoreModule.forRoot([]), EffectsModule.forRoot([]), StoreAuthModule, StoreBusinessUsersModule, StoreNotificationsModule),
    provideAnimationsAsync(),
    provideHttpClient(),
    importProvidersFrom(NzModalModule),
    importProvidersFrom(
      AuthModule.forRoot({
        domain: environment.auth0.domain,
        clientId: environment.auth0.clientId,
        authorizationParams: {
          redirect_uri: window.location.origin + "/callback",
          audience: environment.auth0.audience,
          scope: "openid profile email offline_access",
        },
        httpInterceptor: {
          allowedList: [
            {
              uri: `${environment.apiUrl}/*`,
              tokenOptions: {
                authorizationParams: {
                  audience: environment.auth0.audience,
                  scope: "openid profile email offline_access",
                },
              },
            },
          ],
        },
        useRefreshTokens: true,
        cacheLocation: 'localstorage',
      })
    ),
    provideHttpClient(withInterceptors([authInterceptor])),
    importProvidersFrom(NzIconModule.forRoot(icons)),
    provideCharts(withDefaultRegisterables()),
    providePostHogErrorHandler(),
    providePostHogPageviewTracking(),
  ],
};
